import React, { useState, useEffect } from "react";
import ArrowUp from "./../../../assets/images/arrow-up.svg";
import ArrowDown from "./../../../assets/images/arrow-down.svg";
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import API from "../../../services";
import { toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import CloseIcon from '@mui/icons-material/Close';
import { RootState } from "../../../models/RootState";
import { connect } from "react-redux";
import { useTranslation } from "../../../hooks/useTranslation";
import CancelIcon from '@mui/icons-material/Cancel';
import OCR_API from "../../../services/ocr";
interface TransactionHistoryItemProps {
  type: string;
  title: string;
  amount?: number;
  date: string;
  status: string;
  referenceId: string;
  onClick: () => void; // Add this line
  activeWallet: string;
  onRedeposit: () => void;
  transaction: any;
  userDetails?: any;
  dynamicTextColors?: any;
}

const TransactionHistoryItem: React.FC<TransactionHistoryItemProps> = ({
  type,
  title,
  amount,
  date,
  status,
  referenceId,
  onClick,
  activeWallet,
  onRedeposit,
  transaction,
  userDetails,
  dynamicTextColors
}) => {
  const { t } = useTranslation();
  
  // Get dynamic text color classes from Redux instead of calling function
  const textColorClass = dynamicTextColors?.pageTextColorClass || '';
  const buttonTextColorClass = dynamicTextColors?.buttonTextColorClass || '';
  
  // Determine the appropriate arrow icon based on the transaction type
  const arrowIcon = type === "DEPOSIT" || type === "CRYPTO_DEPOSIT" ? ArrowDown : ArrowUp;
  const statusColor = status === "success" ? "text-green-500" : "text-red-500";
  const [utrNumber, setUtrNumber] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [openUtr, setOpenUtr] = useState<any>(false);
  const [openProcess, setOpenProcess] = useState<any>(false);
  const paymentType = ["UPI", "BANK_TRANSFER"]
  const [selectedPType, setSelectedPType] = useState<any>("");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [ocrLoading, setOcrLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [preview, setPreview] = useState<string>("");

  // Validate UTR number - must be exactly 12 digits
  const isValidUtr = utrNumber.length === 12 && /^\d{12}$/.test(utrNumber);

  const handleUtrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // For number inputs, we need to handle the length restriction differently
    // Convert to string and ensure it's only digits, then limit to 12 characters
    const numericValue = value.toString().replace(/\D/g, '').slice(0, 12);
    setUtrNumber(numericValue);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const extractUtrFromOcr = async (file: File, depositType: string) => {
    setOcrLoading(true);
    try {
      const paymentMethod = depositType === "BANK_TRANSFER" ? "BANK_TRANSFER" : "UPI";
      const imageBase64 = await fileToBase64(file);

      const formData = new FormData();
      formData.append('image', imageBase64);
      formData.append('image_names', file.name);
      formData.append('image_types', file.type);
      formData.append('request', JSON.stringify({ payment_method: paymentMethod }));

      const response = await OCR_API.post('/ocr/get-utr', formData);

      let rawUtr: any = null;
      let extractedImageUrl = "";

      if (typeof response.data === "string") {
        rawUtr = response.data;
      } else if (response.data && typeof response.data === "object") {
        if ((response.data as any).utr) {
          rawUtr = (response.data as any).utr;
        }
        if ((response.data as any).image_url) {
          extractedImageUrl = (response.data as any).image_url;
        } else if ((response.data as any).imageUrl) {
          extractedImageUrl = (response.data as any).imageUrl;
        }
      }

      if (extractedImageUrl) {
        setImageUrl(extractedImageUrl);
      }

      if (rawUtr != null) {
        const extractedUtr = rawUtr.toString().replace(/\D/g, '').slice(0, 12);
        if (extractedUtr.length > 0) {
          setUtrNumber(extractedUtr);
          toast.success(t("wallet.messages.utrExtractedSuccessfully"));
        } else {
          toast.warning(t("wallet.messages.couldNotExtractUtr"));
        }
      } else {
        toast.warning(t("wallet.messages.couldNotExtractUtr"));
      }
    } catch (error: any) {
      console.error("OCR API Error:", error);
      toast.error(error.response?.data?.message || t("wallet.messages.failedToExtractUtr"));
    } finally {
      setOcrLoading(false);
    }
  };

  const handleProcessScreenshotChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedPType) {
      toast.error(t("transactions.selectPaymentType"));
      e.target.value = '';
      return;
    }

    if (file.type.startsWith('image/')) {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setScreenshotFile(file);
      const depositType = selectedPType === "BANK_TRANSFER" ? "BANK_TRANSFER" : "UPI";
      await extractUtrFromOcr(file, depositType);
    } else {
      toast.error(t("wallet.messages.pleaseUploadImageFile"));
    }
  };

  const handleRemoveProcessScreenshot = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview("");
    setScreenshotFile(null);
    setUtrNumber("");
    setImageUrl("");
    const fileInput = document.getElementById('screenshotUploadProcess') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const response = await API.post(`/account/resubmit-deposit`, {
        refId: referenceId,
        utr: utrNumber,
        tgProviderId: transaction?.tgProviderId,
      });

      const data = await response.data;
      if (data?.status !== "RS_OK") {
        toast.error(data?.errorDescription);
      } else {
        toast.success(t("transactions.messages.success"));
        onRedeposit();
      }

      setOpenUtr(false);
    } catch (error) {
      console.log("There was a problem with the fetch operation.");
    } finally {
      setLoading(false);
    }
  };

  const processDeposit = async () => {
    setLoading(true);

    try {
      const response = await API.post(`/account/processDeposit`, {
        uKey: sessionStorage.getItem("userKey"),
        depositType: selectedPType,
        bankId: "",
        refId: referenceId,
        utr: utrNumber,
        imageUrl: imageUrl,
        tgProviderId: transaction?.tgProviderId,
        accountId: transaction?.pgAccountId,
      });

      const data = await response.data;
      if (data.status !== "RS_OK") {
        toast.error(data.errorDescription);
      } else {
        if (activeWallet === "POINTS") {
          toast.success(t("transactions.messages.transactionSaved"))
        } else {
          toast.success(t("transactions.messages.transactionSavedSwitchWallet"));
        }
        onProcessClose();
        onRedeposit();
      }
    } catch (error) {
      console.log("There was a problem with the fetch operation.");
    } finally {
      setLoading(false);
    }
  };

  const onProcessClose = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setOpenProcess(false);
    setUtrNumber("");
    setSelectedPType("");
    setScreenshotFile(null);
    setPreview("");
    setImageUrl("");
    setOcrLoading(false);
    const fileInput = document.getElementById('screenshotUploadProcess') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  return (
    <>
      <div onClick={onClick} className="flex gap-5 justify-between p-4 mb-2 w-full rounded-lg bg-meitms-color">
        <div className="flex gap-3 items-center font-medium h-[41px]">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-border-color">
            <img
              src={arrowIcon}
              alt={type === "DEPOSIT" || type === "CRYPTO_DEPOSIT" ? t("transactions.deposit") : t("transactions.withdrawal")}
              className="object-contain w-6 h-6"
              style={{
                filter: buttonTextColorClass === 'dark-text' ? 'brightness(0)' : 'brightness(0) invert(1)'
              }}
            />
          </div>
          <div className="flex flex-col justify-center self-stretch my-auto">
            <div className={`text-sm ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>{title}</div>
            {amount && (
              <div className={`mt-1 text-xs ${textColorClass === 'dark-text' ? 'text-black' : 'text-text-color'}`}>{amount}</div>
            )}
          </div>
        </div>
        <div className={`self-start text-xs text-right ${textColorClass === 'dark-text' ? 'text-black' : 'text-text-color'}`}>
          {date}
          <div className={`flex ${(status !== "success" && type === "DEPOSIT") ? "justify-between" : "justify-end"}`}>
            {
              (type === "DEPOSIT" && status !== "success") && (
                <AddCircleRoundedIcon
                  className={`${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                  style={{ color: textColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (status === "initiated"|| status === "rejected" || status === "abandoned" || status === "cancelled") {
                      setOpenProcess(true);
                    } else if (status !== "success") {
                      // Call the original function for other cases except success
                      setOpenUtr(true);
                    }
                  }}
                />
              )
            }
            <div className={`mt-1 text-sm ${statusColor} capitalize`}>{status}</div>
          </div>
        </div>
      </div>
      {
        openUtr && (
          <Dialog
            fullScreen={false}
            open={openUtr}
            onClose={() => setOpenUtr(false)}
            PaperProps={{
              style: {
                position: 'absolute',
                top: '5rem'
              }
            }}
          >
            <div className={`dialog-bg bg-inputbox-color relative p-4 ${textColorClass}`}>
              <CloseIcon
                className={`absolute top-3 right-3 text-2xl font-semibold ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                onClick={() => setOpenUtr(false)}
              />
              <div>
                <div className={textColorClass === 'dark-text' ? 'text-black' : 'text-white'}>
                  {t("transactions.utrResubmission")}
                </div>
                <div>
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                      className="mt-4 w-full max-w-md mx-auto"
                    >
                      <div className="mb-4">
                        <label htmlFor="utrInputResubmit" className={`block mt-3 text-sm ${textColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: textColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                          {t("transactions.utrNumber")}
                        </label>
                        <input
                          type="number"
                          className="px-4 py-3 mt-2.5 w-full text-sm font-medium text-white placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem]"
                          id="utrInputResubmit"
                          placeholder={t("transactions.enterUtrNumber")}
                          value={utrNumber}
                          onChange={handleUtrChange}
                          maxLength={12}
                        />
                      </div>
                      <div className={`text-sm mb-2 ${isValidUtr ? 'text-green-500' : 'text-red-500'}`}>
                        {utrNumber.length === 0 ? t("transactions.utrNumberMustBe12Digits") : 
                         utrNumber.length < 12 ? t("transactions.enterMoreDigits").replace("{count}", String(12 - utrNumber.length)) :
                         isValidUtr ? '' : t("transactions.utrNumberMustContainOnlyDigits")}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className={`p-2.5 w-full text-sm whitespace-nowrap bg-red-600 rounded-lg border border-red-600 border-solid leading-[1.4rem] ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                          onClick={() => setOpenUtr(false)}
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          type="submit"
                          className={`p-2.5 w-full text-sm whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${(loading || !isValidUtr) ? "opacity-50" : ""} ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                          disabled={loading || !isValidUtr}
                        >
                          <span className="submit-label">
                            {loading ? t("common.processing") : t("common.submit")}
                          </span>
                        </button>
                      </div>
                    </form>
                  </>
                </div>
              </div>
            </div>
          </Dialog>
        )
      }
      {
        openProcess && (
          <Dialog
            fullScreen={false}
            open={openProcess}
            onClose={() => onProcessClose()}
            PaperProps={{
              style: {
                position: 'absolute',
                top: '5rem'
              }
            }}
          >
            <div className={`h-dialog-bg bg-inputbox-color relative p-4 ${textColorClass}`}>
              <CloseIcon
                className={`absolute top-3 right-3 text-2xl font-semibold ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                onClick={() => onProcessClose()}
              />
              <div>
                <div className={textColorClass === 'dark-text' ? 'text-black' : 'text-white'}>
                  {t("transactions.deposit")}
                </div>
                <div>
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        processDeposit();
                      }}
                      className="mt-4 w-full max-w-md mx-auto"
                    >
                      <div className="w-full mt-3 mb-3">
                        <select
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs font-medium whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                          value={selectedPType}
                          onChange={(e) => {
                            setSelectedPType(e.target.value);
                            handleRemoveProcessScreenshot();
                          }}
                        >
                          <option value="" disabled>
                            {t("transactions.selectPaymentType")}
                          </option>
                          {paymentType?.map((option: any) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label htmlFor="screenshotUploadProcess" className={`block text-sm ${textColorClass} mb-1`}>
                          {t("wallet.paymentScreenshot")} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            id="screenshotUploadProcess"
                            accept="image/*"
                            onChange={handleProcessScreenshotChange}
                            className="hidden"
                            disabled={!selectedPType || ocrLoading}
                          />
                          <label
                            htmlFor="screenshotUploadProcess"
                            className={`p-2.5 w-full max-w-full text-sm ${buttonTextColorClass} bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] cursor-pointer flex items-center gap-2 ${screenshotFile ? 'justify-between' : 'justify-center'} ${ocrLoading || !selectedPType ? 'opacity-50 cursor-not-allowed' : ''} overflow-hidden`}
                          >
                            <span className={screenshotFile ? "flex-1 truncate min-w-0 overflow-hidden text-ellipsis" : "whitespace-nowrap"}>
                              {ocrLoading ? t("wallet.extractingUtr") : (screenshotFile ? screenshotFile.name : t("wallet.upload"))}
                            </span>
                            {screenshotFile && !ocrLoading && (
                              <CancelIcon
                                className={`cursor-pointer hover:text-red-500 ${buttonTextColorClass} text-lg shrink-0 flex-shrink-0`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveProcessScreenshot();
                                }}
                              />
                            )}
                          </label>
                        </div>
                        {!screenshotFile && (
                          <p className={`text-xs ${textColorClass} mt-1`}>
                            {selectedPType ? t("wallet.pleaseUploadPaymentImage") : t("transactions.selectPaymentType")}
                          </p>
                        )}
                        {preview && (
                          <div className="mt-3 flex justify-center">
                            <img
                              src={preview}
                              alt="Preview"
                              className="max-h-[240px] max-w-[240px] w-full h-auto object-contain rounded-lg border border-border-color shadow-md"
                            />
                          </div>
                        )}
                      </div>
                      {screenshotFile && (
                        <>
                          <div className="mb-4">
                            <label htmlFor="utrInputProcess" className={`block mt-3 text-sm ${textColorClass}`}>
                              {t("transactions.utrNumber")}
                            </label>
                            <input
                              type="number"
                              className={`px-4 py-3 mt-2.5 w-full text-sm font-medium placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem] ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                              id="utrInputProcess"
                              placeholder={t("transactions.enterUtrNumber")}
                              value={utrNumber}
                              onChange={handleUtrChange}
                              maxLength={12}
                            />
                          </div>
                          <div className={`text-sm mb-2 ${isValidUtr ? 'text-green-500' : 'text-red-500'}`}>
                            {!screenshotFile && utrNumber.length > 0 ? t("wallet.screenshotMandatory") :
                              utrNumber.length === 0 ? t("transactions.utrNumberMustBe12Digits") :
                                utrNumber.length < 12 ? t("transactions.enterMoreDigits").replace("{count}", String(12 - utrNumber.length)) :
                                  isValidUtr ? '' : t("transactions.utrNumberMustContainOnlyDigits")}
                          </div>
                        </>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className={`p-2.5 w-full text-sm whitespace-nowrap bg-red-600 rounded-lg border border-red-600 border-solid leading-[1.4rem] ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                          onClick={() => onProcessClose()}
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          type="submit"
                          className={`p-2.5 w-full text-sm whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${(loading || ocrLoading || !screenshotFile || !isValidUtr || selectedPType === "") ? "opacity-50" : ""} ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                          disabled={loading || ocrLoading || !isValidUtr || !screenshotFile || selectedPType === ""}
                        >
                          <span className="submit-label">
                            {loading ? t("common.processing") : ocrLoading ? t("wallet.extractingUtr") : t("common.submit")}
                          </span>
                        </button>
                      </div>
                    </form>
                  </>
                </div>
              </div>
            </div>
          </Dialog>
        )
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    activeWallet: state.userDetails?.activeWallet,
    userDetails: state.userDetails?.userDetails,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

export default connect(mapStateToProps)(TransactionHistoryItem);
